import * as RadixSelect from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from './cn';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  defaultValue?: string;
  onValueChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Radix's `<Select.Item />` REJECTS an empty-string value at runtime:
 * "A <Select.Item /> must have a value prop that is not an empty string."
 *
 * Our codebase has many call sites that pass `{ value: '', label: 'Select…' }`
 * as a "deselect" / "no choice" row. To avoid surgery at every call site,
 * the wrapper translates the empty string to a private sentinel internally
 * and back to '' when calling onValueChange. Callers see plain strings.
 */
const EMPTY_SENTINEL = '__tss_select_empty__';

const fromRadix = (v: string) => (v === EMPTY_SENTINEL ? '' : v);

export function Select({
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder = 'Select…',
  disabled,
  className,
}: SelectProps) {
  // Only translate value/defaultValue to the sentinel when an option
  // actually uses an empty string. Without that match, value='' should
  // behave like "no selection" so the placeholder renders.
  const hasEmptyOption = options.some((o) => o.value === '');
  const toRadix = (v: string | undefined): string | undefined => {
    if (v === '' && hasEmptyOption) return EMPTY_SENTINEL;
    if (v === '') return undefined; // let Radix fall back to placeholder
    return v;
  };

  return (
    <RadixSelect.Root
      value={toRadix(value)}
      defaultValue={toRadix(defaultValue)}
      onValueChange={(v) => onValueChange(fromRadix(v))}
      disabled={disabled}
    >
      <RadixSelect.Trigger
        data-tss-ui=""
        className={cn('tss-select__trigger', className)}
        aria-label={placeholder}
      >
        <RadixSelect.Value placeholder={placeholder} />
        <RadixSelect.Icon className="tss-select__icon">
          <ChevronDown size={16} aria-hidden="true" />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content
          data-tss-ui=""
          className="tss-select__content"
          position="popper"
          sideOffset={4}
        >
          <RadixSelect.Viewport>
            {options.map((opt) => {
              const radixValue = opt.value === '' ? EMPTY_SENTINEL : opt.value;
              return (
                <RadixSelect.Item
                  key={radixValue}
                  value={radixValue}
                  disabled={opt.disabled}
                  className="tss-select__item"
                >
                  <RadixSelect.ItemText>{opt.label}</RadixSelect.ItemText>
                  <RadixSelect.ItemIndicator className="tss-select__indicator">
                    <Check size={14} aria-hidden="true" />
                  </RadixSelect.ItemIndicator>
                </RadixSelect.Item>
              );
            })}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}
